import { z } from 'zod';

import { SharedLib } from '@shared';

import {
  DEFAULT_DOCUMENT_CIRCULATION,
  DOCUMENT_CIRCULATIONS,
} from '@units/document/model/enums/document-circulation.enums.js';
import {
  DOCUMENT_PARSE_STATES,
  isDocumentParseState,
} from '@units/document/model/enums/document-parse-state.enums.js';
import {
  type DocumentParseState,
  type DocumentSort,
} from '@units/document/types/document.types.js';

/**
 * Состояние реестра документов — так, как его несёт адресная строка (`rules/lists-and-filters.mdc`).
 *
 * Весь отбор живёт здесь, а не в состоянии компонента: выборка обязана пережить перезагрузку,
 * работать в back/forward и уезжать ссылкой коллеге. «Все документы, действовавшие на 01.03.2026, у
 * которых разбор прошёл с замечаниями» — это ссылка, которую юрист пересылает, а не
 * последовательность кликов, которую он диктует.
 *
 * **Каждое поле падает в умолчание через `.catch`, а не бросает.** Умолчание (`.default`)
 * подставляет значение там, где параметра **нет**; присутствующее, но бессмысленное —
 * `?sort=salary`, `?page=вторая`, дата, которой не бывает, — проваливает разбор, а проваленный
 * `validateSearch` заменяет экран границей ошибки. Со `replace: true` на каждой смене фильтра битый
 * адрес при этом остаётся в строке, и перезагрузка не помогает. Сервер поступает наоборот и
 * отвечает 422: там неизвестное значение означает, что его прислал клиент, и правдоподобная
 * подстановка спрятала бы дефект.
 */

/** Столько же, сколько принимает наименование документа: искать длиннее нечего. */
const MAX_QUERY = 300;

/**
 * Порядки, которые принимает `GET /documents`, — перечнем значений, а не пар «колонка ± направление».
 *
 * Пара перестала описывать множество, когда появился `relevance` (STORY-046-05): у него нет
 * обратного направления — список, начинающийся с самого непохожего, это не порядок, а шум. Фабрика
 * `SharedLib.sortSchema` разворачивает каждый ключ в `key` и `-key` и потому такое значение выразить
 * не может.
 *
 * **Дрейф от контракта ловится с двух сторон, и обе — на компиляции.** `satisfies` не даёт объявить
 * здесь значение, которого нет в контракте; `Record<DocumentSort, string>` в подписи порядка
 * (`document-sort-field.component.tsx`) не даёт забыть значение, которое в контракте появилось.
 */
export const DOCUMENT_SORTS = [
  'createdAt',
  '-createdAt',
  'title',
  '-title',
  'relevance',
] as const satisfies readonly DocumentSort[];

/** Свежие сверху: реестр открывают, чтобы найти недавно принятое, чаще, чем чтобы читать алфавит. */
export const DEFAULT_DOCUMENT_SORT = '-createdAt';

/**
 * Сколько строк на странице по умолчанию — умолчание **схемы**, а не константа мимо неё.
 *
 * Прежде здесь стояло обратное решение: «не в адресе, это свойство экрана, а не выборки — размер
 * человек не выбирал, его выбрал макет». Оно было верным ровно до тех пор, пока выбора не было.
 * STORY-049-03 вводит выбор, и причина отменяется прямо: то, от чего она защищала («ссылка,
 * открывающаяся у коллеги иначе»), теперь требует обратного — `perPage` в адресе и есть условие, при
 * котором присланная ссылка показывает у коллеги то же самое.
 */
export const DOCUMENTS_PER_PAGE = 25;

/**
 * Размеры, которые предлагает экран, — фиксированный набор, а не произвольное число.
 *
 * Потолок совпадает с серверным `DOCUMENT_PAGE_MAX = 100` (`list-documents.query.ts`): просить
 * больше значило бы получить молча урезанный ответ, в котором счётчик на экране объясняет не то, что
 * показано.
 */
export const DOCUMENT_PER_PAGE_OPTIONS = [25, 50, 100] as const;

/**
 * Состояния разбора — поэлементным whitelist'ом, а не «весь список или ничего».
 *
 * `.catch([])` на массиве целиком отбросил бы **весь** отбор из-за одного незнакомого слова:
 * ссылка, пережившая переименование состояния на сервере, показала бы не суженный список, а полный
 * — то есть тихо ответила бы на другой вопрос. Здесь незнакомое значение выпадает поштучно
 * (`rules/lists-and-filters.mdc` §3).
 *
 * Одиночное значение принимается наравне со списком: `?parse=PARSED`, набранное руками, — это
 * строка, и разбирать её отказом было бы отказом читать собственный формат.
 *
 * Результат приводится к каноническому порядку перечня и тем самым лишается повторов. Это не
 * опрятность: отбор входит в ключ кеша (`rules/tanstack-query.mdc` §3), и `?parse=A,B` против
 * `?parse=B,A` завели бы две записи кеша под один и тот же ответ — с двумя запросами и двумя
 * моментами устаревания.
 */
const parseStates = z
  /*
   * `z.preprocess`, а не `z.unknown().transform(...)`: внутри `z.object` схема, чей вход
   * необязателен, требует ключ явно и отвечает «expected nonoptional» на отсутствующий параметр —
   * то есть роняет экран ровно на пустом адресе. Измерено, а не предположено.
   */
  .preprocess(
    // Возвращаемый тип назван вслух: `Array.isArray` сужает `unknown` до `any[]`, и без этого
    // дальше по цепочке едет `any` — то есть отключённая проверка ровно там, где разбирается
    // недоверенный вход.
    (value): unknown[] =>
      value === undefined ? [] : Array.isArray(value) ? (value as unknown[]) : [value],
    z.array(z.unknown()),
  )
  .transform((values): readonly DocumentParseState[] => {
    const chosen = values.filter(isDocumentParseState);

    return DOCUMENT_PARSE_STATES.filter((state) => chosen.includes(state));
  });

/**
 * Признак допустимости обработки за периметром: «да», «нет» или «неважно».
 *
 * Три состояния, а не два: отсутствие параметра — это «показывать все», а не «показывать
 * запрещённые». Из адреса значение приходит строкой, из сериализатора роутера — настоящим булевым,
 * и схема обязана читать оба: `z.coerce.boolean()` здесь был бы дефектом — он превращает строку
 * `'false'` в `true`, то есть переворачивает фильтр безопасности на присланной ссылке.
 */
const externalProcessingFilter = z
  .union([z.boolean(), z.literal('true'), z.literal('false')])
  .transform((value) => value === true || value === 'true')
  .optional()
  .catch(undefined);

/**
 * «По состоянию на дату» — такой же параметр отбора, как остальные, под каноническим именем `asOf`
 * (`rules/legal-documents.mdc` §15).
 *
 * В реестре это **фильтр**, а не выбор редакции: он отвечает «какие документы действовали в тот
 * день», и его отсутствие значит «все», а не «действующие сегодня». Документ, принятый в августе и
 * вступающий в силу в январе, не должен исчезать из реестра до января.
 *
 * `z.iso.date()`, а не регулярка: он отбрасывает и `2026-13-01`, и 30 февраля — дни, которые
 * выглядят датой и датой не являются.
 */
const asOfFilter = z.iso.date().optional().catch(undefined);

/**
 * О каких документах спрашивают реестр — и **отсутствие параметра здесь не «без фильтра»**.
 *
 * Умолчание — «только в обращении», и это смысл операции, а не удобство: реестр отвечает «с чем мы
 * работаем», а выведенный документ из этого ответа исключён — иначе вывод из обращения не значил бы
 * ничего. Контраст с `externalProcessing` выше намеренный: там молчание — отказ фильтровать, здесь
 * молчание **и есть** вопрос.
 *
 * `WITHDRAWN` — единственный способ найти выведенный документ, чтобы вернуть его в обращение.
 * Реестр, из которого выведенные исчезают бесследно, делает вывод необратимым на практике, как бы
 * ни называлась обратная операция в контракте.
 *
 * `.catch` вместо броска — по общей причине этого файла: незнакомое значение в присланной ссылке
 * падает в умолчание, а не заменяет экран границей ошибки.
 */
const circulationFilter = z
  .enum(DOCUMENT_CIRCULATIONS)
  .catch(DEFAULT_DOCUMENT_CIRCULATION)
  .default(DEFAULT_DOCUMENT_CIRCULATION);

export const documentRegistrySearchSchema = z.object({
  q: z.string().trim().max(MAX_QUERY).catch('').default(''),
  parse: parseStates,
  asOf: asOfFilter,
  externalProcessing: externalProcessingFilter,
  /*
   * `.catch`, как и у соседей: правка адреса руками не должна подменять экран границей ошибки.
   * Значение при этом падает в умолчание, а не в «как получится».
   */
  sort: z.enum(DOCUMENT_SORTS).catch(DEFAULT_DOCUMENT_SORT).default(DEFAULT_DOCUMENT_SORT),
  circulation: circulationFilter,
  page: z.coerce
    .number()
    .int()
    .min(1)
    .catch(SharedLib.DEFAULT_PAGE)
    .default(SharedLib.DEFAULT_PAGE),
  perPage: SharedLib.perPageWhitelistSchema(DOCUMENT_PER_PAGE_OPTIONS, DOCUMENTS_PER_PAGE),
});

export type DocumentRegistrySearch = z.infer<typeof documentRegistrySearchSchema>;

/** Порядок, в котором реестр упорядочивается, — типом контракта, а не строкой. */
export type DocumentRegistrySort = DocumentRegistrySearch['sort'];

/**
 * Что считается **фильтром** — то, смена чего возвращает читателя на первую страницу.
 *
 * Названо здесь, а не по месту вызова, чтобы у вопроса «не забыл ли я сбросить страницу» был один
 * ответ. Порядок сортировки в перечень не входит намеренно: он меняет, как список читается, а не
 * какие в нём строки, и страница под ним остаётся осмысленной. `circulation` входит: он меняет
 * **какие** строки нашлись, и седьмая страница рабочего набора на перечне выведенных — это
 * пустота, которую читают как «ничего не нашлось».
 */
export const DOCUMENT_FILTER_FIELDS = [
  'q',
  'parse',
  'asOf',
  'externalProcessing',
  'circulation',
] as const;
